/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2025 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import ENVs from './ENVs';
import TemplateHelper from './TemplateHelper';

export default class URLs {
  // Get base URL for Backend API Calls
  static getDataUrl () {
    const baseHost = window.location.host;
    const baseHostname = window.location.hostname;

    let dataURL = process.env.REACT_APP_DATA_URL;
    if (dataURL !== undefined) {
      dataURL = dataURL.replace('%BASE_HOST%', baseHost).replace('%BASE_HOSTNAME%', baseHostname);
      return dataURL;
    } else {
      return '/api/';
    }
  }

  static getUserImagesUrl () {
    return `${ENVs.StaticBucketUrl()}/userImages/`;
  }

  static getMachineList () {
    return URLs.getDataUrl().concat('Machines');
  }

  static getFairs () {
    return URLs.getDataUrl().concat('Fairs');
  }

  static getFairMap (fairTag) {
    return URLs.getFairs().concat('/maps/').concat(fairTag);
  }

  static getFairMapBucket (fairTag) {
    return URLs.getUserImagesUrl().concat('fairs/').concat(fairTag).concat('_map.svg');
  }

  static getFullSoftwareImageUrl (imageUrl) {
    let [, typ, swtag, imgtyp] = imageUrl.split('/');
    if (typ === 'images') {
      return `${ENVs.StaticBucketUrl()}/userImages/softwares/${imgtyp}_${swtag}.png`;
    } else {
      return imageUrl;
    }
  }


  static getFullFairLogoUrl (imageUrl) {
    let [, typ, fairTag] = imageUrl.split('/');
    if (typ === 'logos') {
      if(ENVs.StaticBucketUrl() !== '') {
        return `${ENVs.StaticBucketUrl()}/userImages/fairs/${fairTag}_logo.png`;
      } else {
        return URLs.getFairs().concat(imageUrl);
      }
    } else {
      if (imageUrl.startsWith('/userImages')) {
        return `${ENVs.StaticBucketUrl()}${imageUrl}`;
      }
      return imageUrl;
    }
  }

  static getSoftwares () {
    return URLs.getDataUrl().concat('Softwares');
  }

  static getTimelineData () {
    return URLs.getDataUrl().concat('Timeline/TimelineData');
  }

  static getMachineImage () {
    return URLs.getDataUrl().concat('MachineImage/Image');
  }

  static getMachineImageFull (machineId, machineType, deviceClass) {
    const params = new URLSearchParams();
    params.append('machineId', machineId);
    machineType && params.append('machineType', machineType);
    deviceClass && params.append('deviceClass', deviceClass);
    return URLs.getMachineImage().concat(`?${params.toString()}`);
  }

  static MqttBrokerUrl () {
    const envBrokerUrl = import.meta.env.VITE_MQTT_BROKER_URL;
    if (envBrokerUrl) {
      return envBrokerUrl;
    }
    let ret = '';
    const url = new URL(window.location.href);
    ret += (url.protocol === 'https:') ? 'wss' : 'ws';
    ret += '://';
    ret += url.host;
    ret += '/ws/';

    return ret;
  }

  static getDefaultImageNameByClassifier (machineType, imageDefaults, defaultImagePathClassifier) {
    for (const classifierRegex in imageDefaults) {
      if (RegExp(classifierRegex).test(defaultImagePathClassifier)) {
        const imageName = imageDefaults[classifierRegex];
        if (!URLs.getDefaultMachineImageName.cache) {
          URLs.getDefaultMachineImageName.cache = {};
        }
        URLs.getDefaultMachineImageName.cache[machineType + defaultImagePathClassifier] = imageName;
        return imageName;
      }
    }
  }

  static getDefaultMachineImageName (machineType, defaultImagePathClassifier) {
    if (URLs.getDefaultMachineImageName?.cache?.[machineType + defaultImagePathClassifier]) {
      return URLs.getDefaultMachineImageName?.cache?.[machineType + defaultImagePathClassifier];
    }

    const template = TemplateHelper.getTemplateByTypeId(machineType);
    if (template) {
      const imageName = URLs.getDefaultImageNameByClassifier(machineType, template.imagesConfig.imageDefaults, defaultImagePathClassifier);
      if (imageName) {
        return imageName;
      }
    }

    const machineImageDefaults = ENVs.Images().MachineImageDefaults;
    for (const machineTypeRegex in machineImageDefaults) {
      if (RegExp(machineTypeRegex).test(machineType)) {
        return URLs.getDefaultImageNameByClassifier(machineType, machineImageDefaults[machineTypeRegex], defaultImagePathClassifier);
      }
    }
  }

  static getMachineImageBucketDefault (machineType, defaultImagePathClassifier) {
    return `${URLs.getUserImagesUrl()}default/${URLs.getDefaultMachineImageName(machineType, defaultImagePathClassifier)}`;
  }

  static getMachineImageBucket (imageName) {
    return `${URLs.getUserImagesUrl()}${imageName}`;
  }

  static getLogin () {
    return URLs.getDataUrl().concat('Auth/login');
  }

  static getUserinfo () {
    return URLs.getDataUrl().concat('Auth/userinfo');
  }

  static getLogout () {
    return URLs.getDataUrl().concat('Auth/logout');
  }

  static getJobInsert () {
    return URLs.getDataUrl().concat('jobs');
  }

  static getJobAction (actionType) {
    return URLs.getDataUrl().concat('Jobs/action/' + actionType);
  }

  static getAntiforgeryToken(){
    return URLs.getDataUrl().concat('antiforgery/token');
  }
}
